import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoDirective } from '@jsverse/transloco';
import { LOANDEPOT_COPYRIGHT_START_YEAR, LOANDEPOT_NMLS_ID } from '@ld.consumer-portal/constants';
import { CiamMfaEmail, CiamMfaPhone } from '@ld.consumer-portal/models';
import { ThemeService } from '@ld.consumer-portal/utils';

export interface IdentityVerificationSelection {
  phone?: { fieldName: string; maskedValue: string };
  email?: { fieldName: string; maskedValue: string };
}

@Component({
  selector: 'ld-identity-verification-card',
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    TranslocoDirective,
  ],
  templateUrl: './identity-verification-card.component.html',
  styleUrl: './identity-verification-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IdentityVerificationCardComponent {
  private readonly themeService = inject(ThemeService);

  protected readonly logoUrl = computed(() => this.themeService.getLogoUrl());
  protected readonly partnerDisplayName = computed(() => this.themeService.getPartnerDisplayName());
  protected readonly isJvTheme = computed(() => this.themeService.isJvThemeActive());
  protected readonly nmlsCopyright = `NMLS#${LOANDEPOT_NMLS_ID} \u00A9${LOANDEPOT_COPYRIGHT_START_YEAR}-${new Date().getFullYear()} loanDepot.com, LLC.`;

  phones = input<CiamMfaPhone[]>([]);
  emails = input<CiamMfaEmail[]>([]);
  isLoading = input(false);
  externalErrorMessage = input<string | null>(null);
  isCiamEnabled = input(false);
  userEmail = input('');

  selectionConfirmed = output<IdentityVerificationSelection>();
  backToLogin = output<void>();

  selectedPhoneFieldName = signal<string | null>(null);
  selectedEmailFieldName = signal<string | null>(null);

  protected readonly hasPhones = computed(() => this.phones().length > 0);
  protected readonly hasEmails = computed(() => this.emails().length > 0);
  protected readonly hasOptions = computed(() => this.hasPhones() || this.hasEmails());

  protected readonly defaultPhoneFieldName = computed(() => {
    const phoneList = this.phones();
    return phoneList.length > 0 ? phoneList[0].fieldName : null;
  });

  protected readonly defaultEmailFieldName = computed(() => {
    const emailList = this.emails();
    return emailList.length > 0 ? emailList[0].fieldName : null;
  });

  protected readonly activePhoneFieldName = computed(() => this.selectedPhoneFieldName() ?? this.defaultPhoneFieldName());
  protected readonly activeEmailFieldName = computed(() => this.selectedEmailFieldName() ?? this.defaultEmailFieldName());

  protected readonly canProceed = computed(() => {
    return this.activePhoneFieldName() !== null || this.activeEmailFieldName() !== null;
  });

  onPhoneSelect(fieldName: string): void {
    this.selectedPhoneFieldName.set(fieldName);
  }

  onEmailSelect(fieldName: string): void {
    this.selectedEmailFieldName.set(fieldName);
  }

  onProceed(): void {
    const selection: IdentityVerificationSelection = {};

    const phoneFieldName = this.activePhoneFieldName();
    if (phoneFieldName) {
      const phone = this.phones().find(p => p.fieldName === phoneFieldName);
      if (phone) {
        selection.phone = { fieldName: phone.fieldName, maskedValue: phone.maskedValue };
      }
    }

    const emailFieldName = this.activeEmailFieldName();
    if (emailFieldName) {
      const email = this.emails().find(e => e.fieldName === emailFieldName);
      if (email) {
        selection.email = { fieldName: email.fieldName, maskedValue: email.maskedValue };
      }
    }

    if (!selection.phone && !selection.email) return;

    this.selectionConfirmed.emit(selection);
  }

  onPhoneSelectNative(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedPhoneFieldName.set(target.value);
  }

  onEmailSelectNative(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.selectedEmailFieldName.set(target.value);
  }

  onBack(): void {
    this.backToLogin.emit();
  }
}
